import dayjs, { Dayjs } from 'dayjs';

/**
 * Backend date-only fields — booking/space availability, campaign timelines —
 * are transported as UTC *day markers*: start-of-day `00:00:00Z` and
 * end-of-day `23:59:59Z`. Verified against api.test.generatik.com, where a
 * media-plan booking round-trips as
 * `{ start: "2026-08-08T00:00:00Z", end: "2026-09-06T23:59:59Z" }`.
 *
 * A day marker denotes a CALENDAR DAY, not an instant, so it has to be read in
 * UTC. Formatting one in the viewer's local time shifts the rendered day:
 * an end-of-day `23:59:59Z` rolls to the NEXT day everywhere east of UTC
 * (the reported +1-day bug, seen at UTC+3), and a start-of-day `00:00:00Z`
 * rolls to the PREVIOUS day everywhere west of it.
 *
 * This is deliberately a narrow predicate rather than a blanket "render every
 * date in UTC": genuine timestamps (`createdAt`, `rejectedAt`, `delayedAt`, …)
 * are real instants and must keep rendering in the viewer's local time. Only a
 * value sitting exactly on a UTC day boundary is treated as a marker — real
 * event timestamps effectively never do.
 *
 * Note that a bare `YYYY-MM-DD` or an unzoned `…T00:00:00` is parsed by dayjs
 * as LOCAL midnight, so it is not a UTC marker and correctly stays on the
 * local path.
 */
function isUtcDayBoundary(utcValue: Dayjs): boolean {
  const startOfDay =
    utcValue.hour() === 0 &&
    utcValue.minute() === 0 &&
    utcValue.second() === 0 &&
    utcValue.millisecond() === 0;
  // End markers arrive as `23:59:59` (backend) or `23:59:59.999`
  // (`localDateToUtcEnd`), so the millisecond part is not constrained here.
  const endOfDay = utcValue.hour() === 23 && utcValue.minute() === 59 && utcValue.second() === 59;
  return startOfDay || endOfDay;
}

/**
 * Returns the dayjs instance whose calendar date is the one the user picked:
 * UTC-anchored for a UTC day marker (see {@link isUtcDayBoundary}), local for
 * everything else. Format the result with a date-only pattern.
 */
export function asCalendarDay(value: string | Date): Dayjs {
  const local = dayjs(value);
  if (!local.isValid()) return local;
  const utcValue = local.utc();
  return isUtcDayBoundary(utcValue) ? utcValue : local;
}

export function localDateToUtcStart(dateStr: string): string {
  return dayjs(dateStr).startOf('day').utc().toISOString();
}

export function localDateToUtcEnd(dateStr: string): string {
  return dayjs(dateStr).endOf('day').utc().toISOString();
}

export function utcToLocalDateStr(isoString: string): string {
  return dayjs(isoString).local().format('YYYY-MM-DD');
}

export const normDate = (d: string) => d.split('T')[0];

export const datesOverlap = (s1: string, e1: string, s2: string, e2: string) =>
  normDate(s1) <= normDate(e2) && normDate(s2) <= normDate(e1);
